// [split W1-B] The platform's own identity (control plane), distinct from any
// tenant. Mirrors backend services/platformIdentity.ts (PLATFORM_NAME).
//
// "Boaz AI" is the PLATFORM — the control plane that hosts every tenant. Its
// name appears ONLY on the platform's OWN surfaces: the sign-in screen served
// from the platform's DEDICATED host (backend PLATFORM_HOST → is_platform),
// plus operator / console chrome and the demo gate. A tenant custom domain
// shows that tenant's host-resolved branding, and today's unmatched
// founding-default host shows the founding tenant (FNS) — a white-label client,
// and FNS itself, must never see the platform's name on their own sign-in.
//
// Env-overridable via VITE_PLATFORM_NAME (no new REQUIRED env; falls back to
// 'Boaz AI').

import { useEffect, useState } from 'react';
import { PUBLIC_API_BASE } from './api';
import { PUBLIC_BRANDING_DEFAULTS, type PublicBranding } from './publicBranding';

export const PLATFORM_NAME =
  (import.meta.env.VITE_PLATFORM_NAME as string | undefined)?.trim() || 'Boaz AI';

// Login-screen branding for the platform's OWN host (no tenant matched): the
// PLATFORM identity, never a tenant's or the founder's name.
export const PLATFORM_LOGIN_BRANDING: PublicBranding = {
  app_name: PLATFORM_NAME,
  company_name: PLATFORM_NAME,
  tagline: '',
  logo_url: null,
  primary_color: null,
  support_email: PUBLIC_BRANDING_DEFAULTS.support_email,
  ghl_chat_location_id: null,
  ghl_chat_widget_id: null,
};

/**
 * Branding for the SIGN-IN screen, resolved by host against /public/branding.
 * The BACKEND decides the identity for every host and returns populated branding:
 *   • a tenant custom domain            → that tenant's host-resolved branding
 *   • a dedicated platform host          → the PLATFORM (Boaz) identity (opt-in
 *     via backend PLATFORM_HOST; is_platform:true)
 *   • any other host (today's default)   → the FOUNDING tenant's branding, so
 *     the current single-domain deployment shows "FNS AI" exactly as before the
 *     split — a founder/staff on FNS's own domain never sees the platform name.
 * The frontend just renders whatever the endpoint returns; the FOUNDING (FNS)
 * default (PUBLIC_BRANDING_DEFAULTS, env-overridable) shows only briefly before
 * the fetch resolves (or if the API is unreachable), so FNS's sign-in stays
 * byte-identical and the platform name never appears on a tenant/founding surface.
 */
export function useSignInBranding(): PublicBranding {
  const [branding, setBranding] = useState<PublicBranding>(PUBLIC_BRANDING_DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    const host = encodeURIComponent(window.location.hostname);
    fetch(`${PUBLIC_API_BASE}/public/branding?host=${host}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const b = d?.branding as Partial<PublicBranding> | undefined;
        if (cancelled || !b?.app_name) return; // nothing usable → keep the founding (FNS) default
        setBranding({
          app_name: b.app_name,
          company_name: b.company_name || b.app_name,
          tagline: b.tagline ?? '',
          logo_url: b.logo_url ?? null,
          primary_color: b.primary_color ?? null,
          support_email: b.support_email ?? PUBLIC_BRANDING_DEFAULTS.support_email,
          ghl_chat_location_id: b.ghl_chat_location_id ?? null,
          ghl_chat_widget_id: b.ghl_chat_widget_id ?? null,
        });
        // Keep the browser tab in sync with the sign-in brand. Pre-login,
        // usePageTitle() hasn't run yet, so without this the tab keeps the
        // static index.html title ("FNS AI") even on the platform/other-tenant
        // host. Match the tab to whatever the sign-in screen actually shows.
        document.title = b.app_name;
      })
      .catch(() => { /* founding default is always a safe render */ });
    return () => { cancelled = true; };
  }, []);

  return branding;
}
